import useSWR from "swr";
import {
    getInterpretationData,
    getInterpretationGraphs,
    getInterpretationTables,
} from "../api/interpretation";
import { InterpretationDataFromAPI } from "../api/types";
import { InterpretationLevel } from "../routes/test/interpretation";
import { TestGraph } from "../types";
import useGraphsFactory from "./useGraphsFactory";
import useTestId from "./useTestId";
import { useSWRAlwaysRevalidate } from "./useSWR_allwaysRevalidate";

export function useInterpretationData() {
    const testId = useTestId();

    const swrKey = testId
        ? JSON.stringify({
              endpoint: "interpretation-data",
              testId,
          })
        : null;

    const fetcher = async () => {
        return await getInterpretationData({
            testId,
            refresh: false,
        });
    };

    const {
        data: value,
        error,
        mutate,
        isLoading: loading,
    } = useSWR(swrKey, fetcher, {
        revalidateOnMount: true,
        revalidateIfStale: true,
    });

    const refresh = async () => {
        // Call API with refresh: true and update cache
        const freshData = await getInterpretationData({
            testId,
            refresh: true,
        });
        await mutate(freshData, false);
        return freshData;
    };

    return { value, loading, error, refresh, refetch: mutate };
}

export function useInterpretationGraphs({
    interpretationLevel,
    interpretationData,
}: {
    interpretationLevel: InterpretationLevel;
    interpretationData: InterpretationDataFromAPI | undefined;
}) {
    const testId = useTestId();

    // Create a unique key for the SWR cache
    const swrKey =
        testId && interpretationData
            ? `graphs:interpretation-${JSON.stringify({
                  testId,
                  interpretationLevel,
                  interpretationData,
              })}`
            : null;

    const fetcher = async () => {
        const newData = await getInterpretationGraphs({
            testId,
            interpretationLevel,
        });
        return newData as TestGraph[];
    };

    return useGraphsFactory(fetcher, swrKey, {
        alwaysRevalidate: true,
    });
}

export function useInterpretationTable({
    interpretationLevel,
    interpretationData,
}: {
    interpretationLevel: InterpretationLevel;
    interpretationData: InterpretationDataFromAPI | undefined;
}) {
    const testId = useTestId();

    const swrKey =
        testId && interpretationData
            ? JSON.stringify({
                  endpoint: "interpretation-tables",
                  testId,
                  interpretationLevel,
                  interpretationData,
              })
            : null;

    const fetcher = async () => {
        const tablesData = await getInterpretationTables({
            testId,
            interpretationLevel,
        });
        return tablesData;
    };

    const {
        data: value,
        error,
        mutate,
        isLoading: loading,
    } = useSWRAlwaysRevalidate(swrKey, fetcher);

    return { value, loading, error, refetch: mutate };
}
